import type { CanvasView, ShotView, StoryboardView } from "@dramapie/shared";

/**
 * 数据 tab 的三份投影（原型 `dataPane` 2452 / Wiki D-01）。
 *
 * 原型强调「领域数据是唯一真源，故事板与画布都是投影」：
 * - 领域数据：一镜的领域字段（顺序 / 场 / 台词 / 角色 / 帧），**不含位置**；
 * - 故事板投影：按 `scene.order → shot.order` 拍平，只留审核需要的字段；
 * - 画布投影：同一批镜头 + `layout`（x/y 只在视图层）+ 边。
 *
 * 全部由服务端投影（`/view`、`/canvas`）派生，前端不新造数据。
 */

export type DataTabId = "domain" | "storyboard" | "canvas";

export interface DataTabDef {
  id: DataTabId;
  label: string;
  note: string;
}

export const DATA_TABS: DataTabDef[] = [
  {
    id: "domain",
    label: "领域数据",
    note: "唯一真源。镜头只存 order / sceneId / dialogue / chars / keyframe，不存位置。",
  },
  {
    id: "storyboard",
    label: "故事板投影",
    note: "故事板投影：按 scene.order → shot.order 拍平，只取顺序和审核需要的字段。",
  },
  {
    id: "canvas",
    label: "画布投影",
    note: "画布投影：同一批镜头 + layout（x/y 只在视图层）+ 边（sequence / derives / references）。",
  },
];

/** 领域数据：优先当前选中的镜头，没有就取第一镜。 */
export function domainProjection(
  shot: ShotView | null,
  view: StoryboardView | null,
): Record<string, unknown> | null {
  const target =
    shot ?? (view ? [...view.shots].sort((a, b) => a.order - b.order)[0] ?? null : null);
  if (!target) return null;

  return {
    id: target.id,
    order: target.order,
    sceneId: target.sceneId,
    duration: target.duration,
    framing: target.framing,
    status: target.status,
    action: target.action,
    dialogue: target.dialogue,
    chars: target.chars,
    keyframe: target.keyframe,
    endFrame: target.endFrame,
    clip: target.clip,
    checks: target.checks,
    costPoints: target.costPoints,
    rev: target.rev,
  };
}

/** 故事板投影：场 → 镜头，只留顺序 / 时长 / 状态 / 台词数。 */
export function storyboardProjection(view: StoryboardView | null): Record<string, unknown> | null {
  if (!view) return null;
  const byId = new Map(view.shots.map((shot) => [shot.id, shot] as const));

  return {
    episode: { id: view.episode.id, no: view.episode.no, title: view.episode.title },
    rev: view.rev,
    stats: view.stats,
    scenes: [...view.scenes]
      .sort((a, b) => a.order - b.order)
      .map((scene) => ({
        scene: scene.name,
        sceneId: scene.id,
        order: scene.order,
        time: scene.time,
        shots: scene.shotIds
          .map((shotId) => byId.get(shotId))
          .filter((item): item is ShotView => Boolean(item))
          .sort((a, b) => a.order - b.order)
          .map((item) => ({
            id: item.id,
            order: item.order,
            duration: item.duration,
            framing: item.framing,
            status: item.status,
            dialogue: item.dialogue.length,
            checks: item.checks.length,
          })),
      })),
  };
}

/**
 * 画布投影：位置只在这一层（`rect`），并显式标注「layout 不进领域数据」。
 * 边由 `sequence` 派生：同一场内按 shot.order 串起来，和画布上的连线一致。
 */
export function canvasProjection(canvas: CanvasView | null): Record<string, unknown> | null {
  if (!canvas) return null;

  const scenes = canvas.scenes.map((group) => ({
    sceneId: group.sceneId,
    name: group.name,
    time: group.time,
    rect: group.rect,
    shots: [...group.shots]
      .sort((a, b) => a.shot.order - b.shot.order)
      .map((node) => ({
        shotId: node.shotId,
        order: node.shot.order,
        status: node.shot.status,
        rect: node.rect,
      })),
  }));

  const edges = canvas.scenes.flatMap((group) => {
    const ordered = [...group.shots].sort((a, b) => a.shot.order - b.shot.order);
    return ordered.slice(1).map((node, index) => ({
      kind: "sequence",
      from: ordered[index]?.shotId ?? "",
      to: node.shotId,
      sceneId: group.sceneId,
    }));
  });

  return {
    episode: { id: canvas.episode.id, no: canvas.episode.no, title: canvas.episode.title },
    rev: canvas.rev,
    columns: canvas.columns,
    viewport: canvas.viewport,
    scenes,
    unplaced: canvas.unplaced.map((node) => ({
      shotId: node.shotId,
      rect: node.rect,
    })),
    edges,
    note: "layout（x/y/w/h）只存在于画布视图，不进领域数据。",
  };
}

/** 当前 tab 对应的 JSON 负载（null → 面板给空态）。 */
export function projectionFor(
  tab: DataTabId,
  input: {
    shot: ShotView | null;
    view: StoryboardView | null;
    canvas: CanvasView | null;
  },
): Record<string, unknown> | null {
  if (tab === "domain") return domainProjection(input.shot, input.view);
  if (tab === "storyboard") return storyboardProjection(input.view);
  return canvasProjection(input.canvas);
}
